import { faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@repo/videa-components/src/index';
import { Toaster } from '@repo/videa-components/src/ui/sonner';
import { PermissionStateDetector } from '@repo/videa-notes';
import axios from 'axios';
import { useAtomValue, useSetAtom } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';

import { useHasWriteback } from '@/react-query/videa-notes/useHasWriteback';
import { useDevtoolsShortcut } from '@/utils/hooks/useDevtoolsShortcut';

import { usePreferences } from '../../react-query/usePreferences';
import { mixpanelEvents } from '../../utils/mixpanel';
import { selectDateAtom } from '../patientList/atoms';

import { VideaNotesMainContent } from './components/slideOutViews';
import {
  VideaNotesCoreProvider,
  VideaNotesNoteResultProvider,
  VideaNotesRecordingProvider,
} from './context';
import {
  getNoteSummaryWithoutBlurb,
  useAppointmentData,
  useAppointmentNotes,
  useAudioUpload,
  useCoreContextValue,
  useNoteManagement,
  useNoteResultContextValue,
  usePatientConsent,
  usePromptTemplateSelection,
  useRecordingContextValue,
  useSaveNoteToPMS,
  useVideaNotesAnalytics,
  useVideaNotesEventHandlers,
  useVideaNotesUIAtoms,
} from './hooks';
import { monitoringStreamAtom } from './state';

export { getNoteSummaryWithoutBlurb };

export const VideaNotesSheet = () => {
  // Redux selectors
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;
  const displayPhi = user?.privacyOn;

  // Feature flags/preferences
  const { data: preferences } = usePreferences();
  const isVideaNotesEnabled = preferences?.isVideaNotesEnabled;

  // Dev tools shortcut (r+b+l) for hidden features
  const { showDevtools } = useDevtoolsShortcut();

  // Grouped UI atom hook
  const {
    uiStatus,
    setUiStatus,
    isSheetOpen,
    setIsSheetOpen,
    isCancelRecordingConfirmationShown,
    setIsCancelRecordingConfirmationShown,
    voiceNotesSource,
    recorderStatus,
    mediaBlobUrl,
  } = useVideaNotesUIAtoms();

  // Monitoring stream setter (only functional microphone atom needed here)
  const setMonitoringStream = useSetAtom(monitoringStreamAtom);

  // Appointment data hook
  const {
    patient,
    patientName,
    patientNameForConsentRecord,
    estimatedVisitId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    appointmentStartsAt,
    setAppointmentData: setAppointmentDataForVideaNotes,
  } = useAppointmentData(displayPhi);

  // Template/prompt selection hook
  const {
    promptOptions,
    selectedPrompt,
    setSelectedPrompt,
    templateOptions,
    selectedTemplate,
    setSelectedTemplate,
  } = usePromptTemplateSelection();

  // External data
  const selectedDate = useAtomValue(selectDateAtom);
  const hasWriteback = useHasWriteback(isSheetOpen);
  const hasWritebackEnabled = Boolean(hasWriteback?.data);

  // Custom hooks for feature logic
  const {
    clinicalNotesData,
    isLoading: isLoadingNotes,
    error: notesError,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
  } = useAppointmentNotes({
    unifiedAppointmentId,
    standalonePatientVisitId,
    isSheetOpen,
  });

  const {
    selectedNote,
    noteContent,
    patientConsentBlurb,
    isSavingNoteUpdate,
    isNoteSavedToPMS,
    isNoteBeingSavedToPMS,
    isNoteReadOnly,
    setNoteContent,
    handleSetSelectedNoteId,
    saveNoteBeforeClose,
  } = useNoteManagement({
    clinicalNotesData,
    hasWritebackEnabled,
    enableVoiceNotesConsent: preferences?.enableVoiceNotesConsent ?? false,
  });

  // Analytics hook (builds payload internally, reads microphone atoms internally)
  const { trackVideaNotesEvent } = useVideaNotesAnalytics({
    patient,
    estimatedVisitId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    selectedPrompt,
    selectedDate,
    voiceNotesSource,
  });

  // Patient consent hook
  const {
    patientConsentRecordId,
    handleStartRecording: handleStartRecordingConsent,
    handleStartProviderOnlyRecording: handleStartProviderOnlyRecordingConsent,
    handlePatientConsentDenied: handlePatientConsentDeniedConsent,
  } = usePatientConsent({
    patient,
    patientNameForConsentRecord,
    appointmentStartsAt,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    username: user.username,
    enableVoiceNotesConsent: preferences?.enableVoiceNotesConsent ?? false,
    onStartRecordingSuccess: () =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesStartRecorderClick),
    onStartProviderOnlyRecordingSuccess: () =>
      trackVideaNotesEvent(
        mixpanelEvents.videaNotesStartProviderOnlyRecorderClick,
      ),
    onPatientConsentDeniedSuccess: () =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesPatientConsentDenied),
  });

  // Audio upload hook
  const { processingError, uploadAudio } = useAudioUpload({
    mediaBlobUrl,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    patientConsentRecordId,
    selectedPrompt,
    selectedTemplate,
    enableVoiceNotesConsent: preferences?.enableVoiceNotesConsent ?? false,
    enableVoiceNotesCustomTemplates:
      preferences?.enableVoiceNotesCustomTemplates ?? false,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
    onSetUiStatus: setUiStatus,
    onSetIsSheetOpen: setIsSheetOpen,
    onSetSelectedNoteId: handleSetSelectedNoteId,
    onUploadError: () =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesGenerateNotesError),
    onUploadSuccess: (s3UploadTime) =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesS3UploadTime, {
        s3UploadTime,
      }),
    onGenerateSuccess: (generationTime) =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesResultLoadTime, {
        loadTime: generationTime,
      }),
  });

  // PMS save hook
  const { triggerSaveNoteToPMS, isSavingNoteToPMS } = useSaveNoteToPMS({
    currentOrganizationId,
    currentPracticeId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    selectedNoteId: selectedNote?.id ?? undefined,
    noteContent,
    hasWritebackEnabled,
  });

  // Determine if content should be shown
  const shouldShowContent =
    !isLoadingNotes || (!unifiedAppointmentId && !standalonePatientVisitId);

  // Event handlers hook
  const {
    showCopyTooltip,
    handleSheetOpen,
    handlePatientCreated,
    handleGenerateNote,
    handleCreateAdditionalNote,
    handleCancelRecording,
    handleCancelRecordingConfirmation,
    handlePause,
    handleResume,
    handleSaveNoteToPMS,
    handleRetryWritebackToPMS,
    handleSummaryTextAreaClick,
    handleCopyText,
    handleCloseSheet,
    handleSheetOpenChange,
  } = useVideaNotesEventHandlers({
    setAppointmentDataForVideaNotes,
    setUiStatus,
    setIsSheetOpen,
    setIsCancelRecordingConfirmationShown,
    uiStatus,
    isCancelRecordingConfirmationShown,
    patient,
    appointmentStartsAt,
    estimatedVisitId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    username: user.username,
    trackVideaNotesEvent,
    uploadAudio,
    triggerSaveNoteToPMS,
    triggerRetryWriteback: triggerSaveNoteToPMS, // Same operation as initial save
    saveNoteBeforeClose,
  });

  // Context value hooks
  const coreContextValue = useCoreContextValue({
    uiStatus,
    voiceNotesSource,
    patientName,
    patient,
    standalonePatientVisitId,
    isVoiceNotesConsentEnabled: preferences?.enableVoiceNotesConsent ?? false,
    isVoiceNotesCustomTemplatesEnabled:
      preferences?.enableVoiceNotesCustomTemplates ?? false,
    processingError,
    onCloseSheet: handleCloseSheet,
    onPatientCreated: handlePatientCreated,
  });

  const recordingContextValue = useRecordingContextValue({
    recorderStatus,
    mediaBlobUrl,
    promptOptions,
    selectedPrompt,
    templateOptions,
    selectedTemplate,
    onSelectedPrompt: setSelectedPrompt,
    onSelectedTemplate: setSelectedTemplate,
    isAudioUploadEnabled: true,
    showDevTools: showDevtools,
    onMonitoringStreamChange: setMonitoringStream,
    onCancelRecording: handleCancelRecording,
    onCancelRecordingConfirmation: handleCancelRecordingConfirmation,
    onPauseRecording: handlePause,
    onResumeRecording: handleResume,
    onStartRecording: handleStartRecordingConsent,
    onStartProviderOnlyRecording: handleStartProviderOnlyRecordingConsent,
    onGenerateNote: handleGenerateNote,
    onPatientConsentDenied: handlePatientConsentDeniedConsent,
  });

  const noteResultContextValue = useNoteResultContextValue({
    clinicalNotesData,
    selectedNote,
    noteContent,
    patientConsentBlurb,
    isNoteReadOnly,
    isNoteSavedToPMS,
    isNoteBeingSavedToPMS,
    isSavingNoteUpdate,
    isSavingNoteToPMS,
    isRetryingWriteback: isSavingNoteToPMS, // Same state as saving
    hasWritebackEnabled,
    showCopyTooltip,
    onNoteChange: setNoteContent,
    onNoteClick: handleSummaryTextAreaClick,
    onSelectedNote: handleSetSelectedNoteId,
    onCopyText: handleCopyText,
    onCreateAdditionalNote: handleCreateAdditionalNote,
    onSaveNoteToPMS: handleSaveNoteToPMS,
    onRetryWritebackToPMS: handleRetryWritebackToPMS,
  });

  // Don't render if feature is disabled
  if (!isVideaNotesEnabled) {
    return null;
  }

  return (
    <>
      <PermissionStateDetector onPermissionChange={() => {}} />
      <Sheet open={isSheetOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          onOpenAutoFocus={handleSheetOpen}
          className="vai-max-h-screen vai-flex vai-flex-col !vai-w-[60vw] vai-min-w-[800px] vai-z-[200] vai-font-inter vai-overflow-hidden"
          aria-describedby=""
          data-testid="videa-notes-sheet"
        >
          <SheetHeader className="vai-flex vai-justify-between vai-bg-gray-100 vai-outline vai-outline-1 vai-outline-slate-300">
            <SheetTitle className="vai-font-inter vai-typography-p vai-text-zinc-700 vai-px-4 vai-py-3">
              <div className="vai-relative vai-flex vai-items-center vai-gap-4">
                <FontAwesomeIcon
                  className="vai-text-[#4A4F55]"
                  icon={faMicrophone}
                />
                <Trans>Voice Note</Trans>
              </div>
            </SheetTitle>
          </SheetHeader>

          <div className="vai-flex-1 vai-px-4 vai-font-inter vai-flex vai-flex-col vai-min-h-0 vai-max-h-full vai-overflow-hidden">
            {/* Loading state */}
            {isLoadingNotes &&
              (unifiedAppointmentId || standalonePatientVisitId) && (
                <p>Loading notes...</p>
              )}

            {/* Error state */}
            {axios.isAxiosError(notesError) && (
              <p className="text-destructive">
                {notesError.response?.data?.message ?? 'An error occurred'}
              </p>
            )}

            {/* Main content with context providers */}
            {shouldShowContent && (
              <VideaNotesCoreProvider value={coreContextValue}>
                <VideaNotesRecordingProvider value={recordingContextValue}>
                  <VideaNotesNoteResultProvider value={noteResultContextValue}>
                    <VideaNotesMainContent />
                  </VideaNotesNoteResultProvider>
                </VideaNotesRecordingProvider>
              </VideaNotesCoreProvider>
            )}
          </div>
          <Toaster position="top-right" />
        </SheetContent>
      </Sheet>
    </>
  );
};
